import type { Voice } from '@listening/api-contract/legacy-client-types';
import { ComparisonTable } from '@/components/astro/new-design-25/SEO/comparison-table';
import { FeatureHighlight } from '@/components/astro/new-design-25/SEO/FeatureHighlight';
import { LinksSrOnly } from '@/components/astro/new-design-25/SEO/LinksSrOnly';
import { PdfToAudioConverter } from '@/components/astro/new-design-25/SEO/pdf-to-audio-converter';
import { SeoContent } from '@/components/astro/new-design-25/SEO/seo-content';
import { SolutionBenefits } from '@/components/astro/new-design-25/SEO/solution-benefits';
import { UseCases } from '@/components/astro/new-design-25/SEO/use-cases';
import { FAQ } from '@/components/astro/new-design-25/ui/FAQ';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { NavBar } from '@/components/astro/new-design-25/ui/NavBar';
import { Testimonials } from '@/components/astro/new-design-25/ui/Testimonials';
import { TryListeningBanner } from '@/components/astro/new-design-25/ui/TryListeningBanner';
import { CategoryCard, toolCategories } from '@/routes/_marketing/tools';

const comparisonTableData = {
  title: 'Why Choose Listening for PDF to Audio',
  description:
    'Listening is purpose-built for academic and professional PDFs, with accurate technical pronunciation and intelligent handling of citations.',
  columns: [
    { header: 'Listening', isHighlighted: true },
    { header: 'Other PDF readers' },
  ],
  rows: [
    {
      feature: 'Academic Vocabulary',
      description: 'Accurate pronunciation of scientific and technical terms.',
      values: ['95% accuracy', '70-80% accuracy'],
    },
    {
      feature: 'Smart Citation Handling',
      description: 'Skip reference numbers and footnotes for smooth flow.',
      values: ['Built-in', 'Reads them aloud'],
    },
    {
      feature: 'Multilingual Voices',
      description: 'Native voices in 8 languages for global research.',
      values: ['8 languages', '1-2 languages'],
    },
    {
      feature: 'Long-Form Audio',
      description: 'Consistent quality across full chapters and papers.',
      values: ['Tuned for long-form', 'Breaks down on length'],
    },
    {
      feature: 'Cross-Platform',
      description: 'Web, iOS, Android, and Chrome extension.',
      values: ['All platforms', 'Web only'],
    },
    {
      feature: 'Speed Control',
      description: 'Adjustable playback for skimming or careful review.',
      values: ['0.5x to 4x', '1x only'],
    },
  ],
};

const DEFAULT_FAQS = [
  {
    question: 'How does the PDF to audio converter work?',
    answer:
      'Drop a PDF into the converter on this page. Your browser extracts the text from the first 10 pages (up to 25,000 characters), then we generate natural audio using your chosen AI voice. The PDF file itself never leaves your browser.',
  },
  {
    question: 'What kinds of PDFs work best?',
    answer:
      "Text-based PDFs (research papers, articles, ebooks, textbook chapters) work best. Image-only or scanned PDFs that have not been OCR'd cannot be read here. The full Listening app handles scanned PDFs via OCR.",
  },
  {
    question: 'What are the free preview limits?',
    answer:
      'The free preview is capped at 10 pages, 25,000 characters, and a 25 MB file size per generation. Sign up for the Listening app to convert full PDFs with no caps.',
  },
  {
    question: 'Can I download the audio?',
    answer:
      'No. The free public tools now create a 24-hour Listening link instead of a downloadable file. The link opens a PDF/read-along view with audio, speed controls, and the playbar.',
  },
  {
    question: 'Is my PDF stored anywhere?',
    answer:
      'No. Text extraction happens entirely in your browser using pdf.js. We never receive the PDF file. We do receive the extracted text to generate audio.',
  },
];

type Feature = { title: string; description: string };

type UseCaseEntry = {
  title: string;
  challenge: string;
  solutionSections: Array<{ label: string; content: string }>;
};

type SeoSection = {
  title: string;
  content: string;
  contentSecondary?: string;
};

type BenefitItem = { text: string; highlight?: string };

type FaqItem = { question: string; answer: string };

type Props = {
  converterHeading?: string;
  converterSubtitle?: string;
  preferredLang?: Voice['lang'];
  preferredGender?: Voice['gender'];
  featureBadge?: string;
  featureTitle?: string;
  featureSubtitle?: string;
  featureDescription?: string;
  featureCtaText?: string;
  featureVariant?: 'default' | 'gradient';
  featureFeatures?: Array<Feature>;
  solutionBenefitsData?: {
    title: string;
    items: Array<BenefitItem>;
  };
  useCasesData?: {
    title: string;
    description: string;
    useCases: Array<UseCaseEntry>;
  };
  seoSections?: Array<SeoSection>;
  faqTitle?: string;
  faqDescription?: string;
  faqs?: Array<FaqItem>;
};

export function PdfToAudioDemoPage(props: Props) {
  const {
    converterHeading,
    converterSubtitle,
    preferredLang,
    preferredGender,
    featureBadge,
    featureTitle,
    featureSubtitle,
    featureDescription,
    featureCtaText,
    featureVariant,
    featureFeatures,
    solutionBenefitsData,
    useCasesData,
    seoSections,
    faqTitle = 'PDF to audio FAQ',
    faqDescription = 'Quick answers about the free PDF to audio converter.',
    faqs = DEFAULT_FAQS,
  } = props;

  return (
    <>
      <a
        className='sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4'
        href='#main-content'
      >
        Skip to main content
      </a>
      <LinksSrOnly />

      <div className='relative bg-white'>
        <NavBar />
        <main id='main-content' tabIndex={-1}>
          <PdfToAudioConverter
            heading={converterHeading}
            preferredGender={preferredGender}
            preferredLang={preferredLang}
            subtitle={converterSubtitle}
          />
        </main>
      </div>

      {featureTitle && (
        <FeatureHighlight
          badge={featureBadge}
          ctaText={featureCtaText}
          description={featureDescription}
          features={featureFeatures}
          subtitle={featureSubtitle}
          title={featureTitle}
          variant={featureVariant}
        />
      )}

      {solutionBenefitsData && (
        <SolutionBenefits
          items={solutionBenefitsData.items}
          title={solutionBenefitsData.title}
        />
      )}

      {useCasesData && (
        <UseCases
          _useCases={useCasesData.useCases}
          description={useCasesData.description}
          title={useCasesData.title}
        />
      )}

      <div className='hidden sm:block'>
        <ComparisonTable
          columns={comparisonTableData.columns}
          description={comparisonTableData.description}
          rows={comparisonTableData.rows}
          title={comparisonTableData.title}
        />
      </div>

      {seoSections && <SeoContent sections={seoSections} />}

      <section
        aria-labelledby='pdf-more-tools-heading'
        className='bg-white px-6 py-16 text-[#0f0e0c] sm:px-8 lg:py-20'
      >
        <div className='mx-auto max-w-[1320px]'>
          <div className='mb-9 max-w-3xl'>
            <p className='font-mono text-[12px] leading-none font-medium tracking-[0.06em] text-[#8e887d] uppercase'>
              Use cases
            </p>
            <h2
              className='mt-3 text-[clamp(34px,4vw,56px)] leading-[1] font-bold tracking-[-0.035em] text-balance text-[#0f0e0c]'
              id='pdf-more-tools-heading'
            >
              Even more ways to use Listening
            </h2>
            <p className='mt-4 max-w-2xl text-[17px] leading-[1.55] text-[#5a564e]'>
              Explore focused landing pages for PDFs, articles, research papers,
              mobile read aloud, study workflows, and accessibility.
            </p>
          </div>
        </div>
        <div className='mx-auto grid max-w-[1320px] grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-3'>
          {toolCategories.map((category) => (
            <CategoryCard category={category} key={category.id} />
          ))}
        </div>
      </section>

      <Testimonials />

      <div
        className='bg-linear-to-b from-azure-525/5 to-azure-525/20'
        role='complementary'
      >
        <FAQ
          description={faqDescription}
          faqs={faqs}
          showApplyButton={false}
          title={faqTitle}
        />
        <TryListeningBanner />
        <Footer />
      </div>
    </>
  );
}
